import { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { ApolloQueryResult } from '@apollo/client';

import { snackbar } from '@occc-app/design-system';
import { LoadingOverlay } from '@occc/ui-design-system';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import {
  CONFIGURE_PAGE_STATE,
  ConfigureFormInitialValues,
  ConfigureFormProps,
  ConfigureFormRenderProps,
  ConfigureFormValues,
  AWARD_TYPES,
} from '@occc-app/routes/AwardCatalog/types';
import {
  Custom_Data_Mode,
  CustomDataInput,
  Offer_Engagement_Type,
  OfferFragment,
  OfferQuery,
  User_Role,
} from '@occc/graphql';
import { useExecuteOperations } from '@occc-app/offerHelpers/operations/useExecuteOperations';
import { useOfferUpdateMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import {
  getDefaultTimezone,
  getFulfillmentChannelInitialValues,
  getPaymentMethodInitialValues,
  getVoucherStackingInitialValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';
import {
  getIsAnyPayAtPumpFieldDirty,
  getOfferUpdateInputForPromptAtPump,
  getOfferUpdateInputToClearPromptAtPump,
  getPromptAtPumpInitialValues,
} from '@occc-app/routes/CampaignComponents/PromptAtPump/utils';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { getCustomDataFromFormValues, isFieldDirty } from '@occc-app/offerHelpers/utils';
import {
  useIsFuelRollbackVoucherStackingEnabled,
  useRollbackStackingConfig,
} from '@occc-app/Chain/hooks';
import { useUserSelector } from '@occc-app/App/AppContext';
import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getAwardCountInitialValues } from '@occc/feat-limits';

import {
  getDefaultFormValues,
  getIsSupportPromptAtPump,
  mapEarningMechanismToConfigureFormValues,
  validateConfigureForm,
} from './utils';
import { ConfigureFormContent } from './ConfigureFormContent';
import { getEarningMechanismOperations } from './helpers/getEarningMechanismOperations';
import { getConstraintOperations } from './helpers/getConstraintOperations';
import { getLimitsOperations } from './helpers/getLimitsOperations';
import { getOfferStackableUpdateOperations } from './helpers/getOfferStackableUpdateOperations';

import { getIsTargetTabEnabled } from '../../utils';

type ConfigureFormContainerProps = {
  award: OfferFragment;
  refetchAward: () => Promise<ApolloQueryResult<OfferQuery>>;
  setPageState?: (value: CONFIGURE_PAGE_STATE) => void;
  awardType: AWARD_TYPES;
  setAwardType: (type: AWARD_TYPES) => void;
  handleCancel: (hasUnsavedChanges?: boolean) => void;
  currentFlowType?: Flow_Types;
};

export const ConfigureFormContainer = ({
  award,
  refetchAward,
  setPageState,
  awardType,
  setAwardType,
  handleCancel,
  currentFlowType = Flow_Types.Update,
}: ConfigureFormContainerProps) => {
  const currentUser = useUserSelector();
  const isReadOnlyRole = currentUser?.userRole === User_Role.AdminReadOnly;
  const previouslyConfigured = award.constraints.length > 0;

  const [offerUpdate] = useOfferUpdateMutation();

  const [executeEarningMechanismOperations, earningMechanismOperationsLoading] =
    useExecuteOperations<ConfigureFormValues>({
      offerId: award.id,
      offer: award,
      getOperations: getEarningMechanismOperations,
    });

  const [executeConstraintsOperations, constraintOperationsLoading] =
    useExecuteOperations<ConfigureFormValues>({
      offerId: award.id,
      offer: award,
      getOperations: getConstraintOperations,
    });

  const [executeLimitsOperations, limitsOperationsLoading] =
    useExecuteOperations<ConfigureFormValues>({
      offerId: award.id,
      offer: award,
      getOperations: getLimitsOperations,
    });

  const [executeOfferStackableUpdateOperations, offerStackableUpdateOperationsLoading] =
    useExecuteOperations<ConfigureFormValues>({
      offerId: award.id,
      offer: award,
      getOperations: getOfferStackableUpdateOperations,
    });

  const [executeNameAndDescriptionOperations, nameAndDescriptionOperationsLoading] =
    useExecuteOperations<ConfigureFormValues>({
      offerId: award.id,
      offer: award,
      getOperations: (offerId, _offer, values, dirtyFields) =>
        getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
    });

  const loading =
    earningMechanismOperationsLoading ||
    constraintOperationsLoading ||
    limitsOperationsLoading ||
    offerStackableUpdateOperationsLoading ||
    nameAndDescriptionOperationsLoading;

  const awardCountInitialValues = getAwardCountInitialValues(award, {
    transaction: {
      isEnabled: false,
    },
  });

  const defaultFormValues = useMemo(() => getDefaultFormValues(), []);

  const rollbackStackingConfig = useRollbackStackingConfig();
  const isFuelRollbackVoucherStackingEnabled = useIsFuelRollbackVoucherStackingEnabled();

  const initialValues: ConfigureFormInitialValues = {
    ...defaultFormValues,
    ...getOfferNameAndDescriptionInitialValues(award),
    ...mapEarningMechanismToConfigureFormValues(award.earningMechanism, award.constraints),
    ...transformOfferToFulfillmentWindowFormValue(award),
    awardCount: awardCountInitialValues,
    paymentMethodConstraint: getPaymentMethodInitialValues(award),
    fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(award),
    voucherStackingConstraint: getVoucherStackingInitialValues(
      award,
      isFuelRollbackVoucherStackingEnabled,
    ),
    promptAtPump: getPromptAtPumpInitialValues(award).promptAtPump,
    customData: transformCustomData(award.customData),
    stackable:
      rollbackStackingConfig === 'PER_OFFER' ? award.stackable : rollbackStackingConfig === 'ALL',
    displayForOrderAhead: award.displayForOrderAhead ?? false,
  };

  const notPreviouslyConfiguredInitialValues: ConfigureFormInitialValues = {
    ...defaultFormValues,
    awardCount: awardCountInitialValues,
    promptAtPump: getPromptAtPumpInitialValues(award).promptAtPump,
    voucherStackingConstraint: getVoucherStackingInitialValues(
      award,
      isFuelRollbackVoucherStackingEnabled,
    ),
    stackable: rollbackStackingConfig === 'PER_OFFER' ? false : rollbackStackingConfig === 'ALL',
    fulfillmentWindow: {
      isEnabled: false,
      timezone: getDefaultTimezone(),
    },
  };

  // TODO: Consider migrating to operations
  const onSubmit = async (values: ConfigureFormValues, form: FormApi<ConfigureFormValues>) => {
    // it's important to take a snapshot of the form state at the beginning of the function as
    // we can lose important properties such as dirtyFields, etc
    const formState = form.getState();

    const getNewEngagementType = () => {
      if (values.awardType === AWARD_TYPES.SWEEPSTAKES) {
        return Offer_Engagement_Type.SweepstakesEntry;
      }
      return Offer_Engagement_Type.Vouchered;
    };

    try {
      const engagementTypeToSet = getNewEngagementType();

      const isEngagementTypeChanged = award.engagementType !== engagementTypeToSet;
      const isAnyPayAtPumpFieldDirty = getIsAnyPayAtPumpFieldDirty(formState.dirtyFields);
      const isSupportPromptAtPump = getIsSupportPromptAtPump(values.awardType);
      const isPromptAtPumpEnabledForOffer = award.optionalAtPos;
      const isNeededToClearPromptAtPump = !isSupportPromptAtPump && isPromptAtPumpEnabledForOffer;

      const customData = getCustomDataFromFormValues(values.customData);
      const customDataInput: CustomDataInput | null = customData
        ? {
            data: customData,
            mode: Custom_Data_Mode.Replace,
          }
        : null;
      const isCustomDataFieldDirty = isFieldDirty<ConfigureFormValues>(
        formState.dirtyFields,
        'customData',
      );

      const isOfferUpdateNeeded =
        isEngagementTypeChanged ||
        isAnyPayAtPumpFieldDirty ||
        isNeededToClearPromptAtPump ||
        isCustomDataFieldDirty;

      if (isOfferUpdateNeeded) {
        const promptAtPumpUpdateInput = isNeededToClearPromptAtPump
          ? getOfferUpdateInputToClearPromptAtPump(award)
          : getOfferUpdateInputForPromptAtPump(award, values);

        await offerUpdate({
          variables: {
            input: {
              id: award.id,
              engagementType: isEngagementTypeChanged ? engagementTypeToSet : undefined,
              ...promptAtPumpUpdateInput,
              customData: customDataInput,
            },
          },
        });
      }

      await executeNameAndDescriptionOperations(values, formState);
      await executeOfferStackableUpdateOperations(values, formState);
      await executeEarningMechanismOperations(values, formState);
      await executeConstraintsOperations(values, formState);
      await executeLimitsOperations(values, formState);
    } catch (e) {
      snackbar.error(e.message);
    }

    if (previouslyConfigured) {
      snackbar.success('Award configuration was updated.');
    }

    await refetchAward();
  };

  const handleNextButtonClick = useCallback(() => {
    if (setPageState) {
      const isTargetTabEnabled = getIsTargetTabEnabled(awardType);
      const nextPage = isTargetTabEnabled
        ? CONFIGURE_PAGE_STATE.TARGET
        : CONFIGURE_PAGE_STATE.DESIGN;

      setPageState(nextPage);
    }
  }, [setPageState, awardType]);

  const configureRenderForm: ConfigureFormProps['render'] = useCallback(
    ({
      handleSubmit,
      values,
      dirty,
      dirtySinceLastSubmit,
      modifiedSinceLastSubmit,
      submitting,
    }: ConfigureFormRenderProps) => {
      const isDirty = modifiedSinceLastSubmit ? dirtySinceLastSubmit : dirty;

      return (
        <OfferForm dirty={!submitting && isDirty} handleSubmit={handleSubmit}>
          <ConfigureFormContent
            values={values}
            setAwardType={setAwardType}
            isReadOnlyRole={isReadOnlyRole}
            currentFlowType={currentFlowType}
            handleCancel={() => handleCancel(isDirty)}
            submitting={submitting}
            dirty={isDirty}
            handleNextButtonClick={handleNextButtonClick}
          />
        </OfferForm>
      );
    },
    [setAwardType, isReadOnlyRole, currentFlowType, handleNextButtonClick, handleCancel],
  );

  return (
    <>
      {loading && <LoadingOverlay rounded />}
      <Form<ConfigureFormValues, ConfigureFormInitialValues>
        onSubmit={onSubmit}
        render={configureRenderForm}
        initialValues={previouslyConfigured ? initialValues : notPreviouslyConfiguredInitialValues}
        validate={validateConfigureForm}
        keepDirtyOnReinitialize
        mutators={{ ...arrayMutators }}
      />
    </>
  );
};
